import type { UseMultipleCallsManagerReturn } from './index.types';
import { useConferenceCallManager } from 'Modules/softphone/hooks/useConferenceCallManager';
import { CallManager } from 'Modules/softphone/services/CallManager';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession } from 'Modules/softphone/types';
import { createLogger } from 'Modules/softphone/utils/logger';
import { useCallback, useEffect, useMemo } from 'react';
import { CONFERENCE_HELD_ID_PREFIX } from './index.constants';

const logger = createLogger('useMultipleCallsManager');

/**
 * Manages multiple concurrent calls with conference awareness.
 *
 * This hook orchestrates the relationship between held calls, the current
 * active call, and conference state. When a conference exists but the user
 * is on a separate 1:1 call, a virtual "Conference call" entry is injected
 * into the held calls list so the user can swap back to the conference.
 *
 * @remarks
 * - The virtual conference call is synced to the store so that
 *   `useSoftphoneContact` can resolve its `displayName` for rendering.
 * - Swapping to the virtual conference entry holds the current 1:1 call
 *   and sets the first conference participant as the active call.
 *
 * @returns {@link UseMultipleCallsManagerReturn} — held calls (with conference),
 *   current call, swap handler, and call count flags.
 */
export const useMultipleCallsManager = (): UseMultipleCallsManagerReturn => {
  const callManager = CallManager.getInstance();

  const { activeCalls, currentCallId, conference, addCall, removeCall } =
    useSoftphoneStore((state) => ({
      activeCalls: state.activeCalls,
      currentCallId: state.currentCallId,
      conference: state.activeConference,
      addCall: state.addCall,
      removeCall: state.removeCall,
    }));

  const { isConferenceActive, participants } = useConferenceCallManager();

  /**
   * Derives the list of held calls excluding the current active call.
   * Sorted by duration descending (longest-held first).
   */
  const heldCalls = useMemo(() => {
    const isHeldCallExcludingCurrent = (call: CallSession) =>
      call.isOnHold && call.id !== currentCallId;

    const calls = Array.from(activeCalls.values())
      .filter(isHeldCallExcludingCurrent)
      .sort((a, b) => b.duration - a.duration);

    logger.debug('Derived held calls', {
      count: calls.length,
      callIds: calls.map((c) => c.id),
      excludedCurrentCallId: currentCallId,
    });

    return calls;
  }, [activeCalls, currentCallId]);

  /**
   * Resolves the current active call session from the store.
   * Returns `null` when no call is active.
   */
  const currentCall = useMemo(() => {
    const call = currentCallId ? activeCalls.get(currentCallId) ?? null : null;

    logger.debug('Derived current call', {
      currentCallId,
      hasCall: !!call,
    });

    return call;
  }, [activeCalls, currentCallId]);

  const hasMultipleCalls = activeCalls.size > 1;
  const heldCallsCount = heldCalls.length;

  // --- Conference-aware held calls ---

  /** Whether the current active call belongs to the conference. */
  const isCurrentCallInConference = Boolean(
    isConferenceActive && currentCall?.conferenceId
  );

  /** Stable ID for the virtual conference held-call entry. */
  const conferenceHeldId = conference
    ? `${CONFERENCE_HELD_ID_PREFIX}${conference.roomGuid}`
    : null;

  const isConferenceOnHold = isConferenceActive && !isCurrentCallInConference;

  /**
   * Virtual conference call session used as a placeholder in the held calls
   * list when the conference is on hold. Built inline so it's available
   * immediately (no useEffect timing gap).
   */
  const virtualConferenceCall = useMemo<CallSession | null>(() => {
    if (!isConferenceOnHold || !conference || !conferenceHeldId) {
      return null;
    }

    return {
      id: conferenceHeldId,
      direction: 'inbound',
      state: 'held',
      phoneNumber: '',
      displayName: 'Conference call',
      startTime: conference.startTime,
      answerTime: conference.startTime,
      endTime: null,
      duration: 0,
      isMuted: false,
      isOnHold: true,
      session: null,
      conferenceId: conference.roomGuid,
    };
  }, [isConferenceOnHold, conference, conferenceHeldId]);

  /**
   * Syncs the virtual "Conference call" session into the store when the
   * conference is on hold. This allows `useSoftphoneContact` to resolve
   * the display name when rendered as a `HeldCallItem`.
   *
   * Cleanup removes the virtual call on unmount or when the conference
   * is no longer on hold.
   */
  useEffect(() => {
    if (virtualConferenceCall) {
      addCall(virtualConferenceCall);
    }

    return () => {
      if (conferenceHeldId) {
        removeCall(conferenceHeldId);
      }
    };
  }, [virtualConferenceCall, conferenceHeldId, addCall, removeCall]);

  /**
   * Builds the held calls list with conference awareness.
   *
   * Filters out individual conference participant calls (they are shown
   * in the conference view instead) and prepends the virtual conference
   * entry when the conference is on hold.
   */
  const heldCallsWithConference = useMemo(() => {
    const nonConferenceCalls = heldCalls.filter((c) => !c.conferenceId);

    if (virtualConferenceCall) {
      return [virtualConferenceCall, ...nonConferenceCalls];
    }

    return nonConferenceCalls;
  }, [heldCalls, virtualConferenceCall]);

  /**
   * Switches the virtual conference held-call entry back to the active
   * conference view by removing the virtual entry and setting the first
   * conference participant as the current call.
   *
   * Sets the conference participant as current immediately so the conference
   * UI appears without waiting for the hold re-INVITE to complete.
   */
  const switchToConference = useCallback(
    (virtualCallId: string) => {
      const store = useSoftphoneStore.getState();
      const currentId = store.currentCallId;
      const targetParticipantId = participants[0].id;

      logger.info('switchToConference', {
        virtualCallId,
        currentId,
        targetParticipantId,
        participantsCount: participants.length,
      });

      // Remove the virtual conference entry AND set the conference participant
      // as current in a single store update. This prevents intermediate renders
      // where currentCallId still points to the 1:1 call (no conferenceId),
      // which would briefly show the normal avatar instead of the conference icon.
      useSoftphoneStore.setState((state) => {
        const newCalls = new Map(state.activeCalls);
        newCalls.delete(virtualCallId);

        return {
          activeCalls: newCalls,
          currentCallId: targetParticipantId,
        };
      });

      // Hold the previous 1:1 call in the background.
      if (currentId) {
        void callManager.holdCall(currentId).catch((error) => {
          logger.error('Failed to hold call during conference switch', {
            callId: currentId,
            error: (error as Error).message,
          });
        });
      }
    },
    [callManager, participants]
  );

  /**
   * Handles swapping to a held call.
   *
   * If the target is a virtual conference entry (identified by the
   * {@link CONFERENCE_HELD_ID_PREFIX}), delegates to {@link switchToConference}.
   * Otherwise falls through to `CallManager.swapCall`.
   *
   * @param callId - The ID of the held call to swap to.
   */
  const handleSwapCall = useCallback(
    (callId: string) => {
      const isConferenceSwap = callId.startsWith(CONFERENCE_HELD_ID_PREFIX);

      logger.info('handleSwapCall called', {
        callId,
        isConferenceSwap,
        participantsCount: participants.length,
        participantIds: participants.map((p) => p.id),
      });

      if (isConferenceSwap && participants.length > 0) {
        switchToConference(callId);
        return;
      }

      if (isConferenceSwap && participants.length === 0) {
        logger.warn(
          'Conference swap requested but no participants found — falling through to swapCall'
        );
      }

      void callManager.swapCall(callId).catch((error) => {
        logger.error('Failed to swap call', {
          callId,
          error: (error as Error).message,
        });
      });
    },
    [callManager, participants, switchToConference]
  );

  return {
    heldCallsWithConference,
    currentCall,
    hasHeldCalls: heldCallsWithConference.length > 0 && currentCall !== null,
    hasMultipleCalls,
    heldCallsCount,
    handleSwapCall,
  };
};
